import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthenticationService } from '../../core/providers/auth/auth.service';
import { environment } from '../../../environments/environment';

export interface SavedView {
  id: string;
  name: string;
  scope: string;
  filters: Record<string, unknown>;
  isDefault: boolean;
  /** Date à laquelle la vue a été cochée par défaut (une vue par défaut par onglet). */
  defaultAt?: string | null;
  context?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class SavedViewsService {
  private readonly url = `${environment.apiUrl}/api/me/saved-views`;

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService<unknown>,
    private router: Router,
  ) {}

  get enabled(): boolean {
    return !!this.auth.currentUserValue?.activeUserFirm;
  }

  get usesOwnFoldersHome(): boolean {
    return ['PROFILE_ACCOUNTANT', 'PROFILE_MISSION_LEADER'].includes(
      this.auth.currentUserValue?.activeProfileCode ?? '',
    );
  }

  get currentUserIri(): string {
    return `/api/users/${this.auth.currentUserValue.id}`;
  }

  normalizeResponsibleFilter(filters: Record<string, unknown>): Record<string, unknown> {
    const resolve = (value: unknown): unknown => value === 'me' || value === '/api/users/me'
      ? this.currentUserIri : value;
    const responsible = filters['responsable'];
    return 'responsable' in filters ? {
      ...filters,
      responsable: Array.isArray(responsible) ? responsible.map(resolve) : resolve(responsible),
    } : filters;
  }

  get scope(): string {
    return this.router.url.split(/[?#]/)[0];
  }

  list(): Observable<SavedView[]> {
    return this.http.get<SavedView[]>(this.url);
  }

  save(view: Omit<SavedView, 'id'>, id?: string): Observable<SavedView[]> {
    return id
      ? this.http.patch<SavedView[]>(`${this.url}/${encodeURIComponent(id)}`, view)
      : this.http.post<SavedView[]>(this.url, view);
  }

  delete(id: string): Observable<SavedView[]> {
    return this.http.delete<SavedView[]>(`${this.url}/${encodeURIComponent(id)}`);
  }

  openHome(): void {
    if (!this.enabled) {
      void this.router.navigate(['/']);
      return;
    }
    this.list().subscribe({
      next: views => {
        // Une vue par défaut par onglet : à la connexion, on ouvre la dernière cochée.
        const view = views.filter(item => item.isDefault)
          .sort((a, b) => (b.defaultAt ?? '').localeCompare(a.defaultAt ?? ''))[0];
        if (view) {
          void this.router.navigate([view.scope], { queryParams: { savedView: view.id } });
        } else {
          this.openMyFolders();
        }
      },
      error: () => this.openMyFolders(),
    });
  }

  private openMyFolders(): void {
    if (!this.usesOwnFoldersHome) {
      void this.router.navigate(['/']);
      return;
    }
    void this.router.navigate(['/my-folders'], {
      queryParams: { responsable: this.currentUserIri },
    });
  }
}
